import { Component } from '@theme/component';

const STORAGE_KEY = 'talescape:age-confirmed-until';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @returns {boolean} Whether this browser confirmed the visitor's age recently.
 */
function isConfirmed() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) > Date.now();
  } catch {
    return false;
  }
}

/**
 * Age check. Asks once, then remembers the answer in this browser for
 * `data-remember-days` days. With `data-trigger="scroll"` it waits until the visitor
 * scrolls a quarter of a screen down; otherwise it asks straight away. In the theme
 * editor it only opens while its section is selected.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The question.
 *
 * @extends Component<Refs>
 */
class AgeGateComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {HTMLElement | undefined} */
  #marker;

  #answered = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog } = this.refs;

    // Escape doesn't answer the question
    dialog.addEventListener('cancel', (event) => event.preventDefault(), { signal });

    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
      return;
    }

    if (isConfirmed()) {
      document.documentElement.classList.remove('age-gate-pending');
      return;
    }

    // Some browsers close a modal after a second Escape. Ask again.
    dialog.addEventListener(
      'close',
      () => {
        if (!this.#answered) requestAnimationFrame(() => this.open());
      },
      { signal }
    );

    if (this.dataset.trigger === 'scroll') {
      this.#openAfterScroll();
    } else {
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#marker?.remove();
  }

  open() {
    const { dialog } = this.refs;
    if (!dialog.open) dialog.showModal();
  }

  /**
   * Remembers the answer and lets the visitor in.
   */
  confirm() {
    const days = Number(this.dataset.rememberDays) || 30;
    this.#answered = true;

    if (!this.hasAttribute('data-design-mode')) {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now() + days * DAY_MS));
      } catch {
        // Storage can be blocked. The visitor gets in for this page view.
      }
    }

    document.documentElement.classList.remove('age-gate-pending');
    this.#observer?.disconnect();
    this.refs.dialog.close();
  }

  /**
   * Sends the visitor away from the shop.
   */
  leave() {
    this.#answered = true;
    window.location.replace(this.dataset.exitUrl || 'https://www.google.com');
  }

  /**
   * Opens once the visitor scrolls a quarter of a screen, so they get a first look at
   * the hero. The marker sits in the normal flow at the top of the page, so it moves
   * with whichever element scrolls the page (Horizon scrolls .page-wrapper, not the
   * window). It's nudged a quarter of a screen down and takes up no space. A page
   * restored mid-scroll asks right away.
   */
  #openAfterScroll() {
    const marker = document.createElement('div');
    marker.setAttribute('aria-hidden', 'true');
    marker.style.cssText =
      'display:block;position:relative;inset-block-start:25vh;width:1px;height:1px;margin-block-end:-1px;pointer-events:none;visibility:hidden;';
    this.append(marker);
    this.#marker = marker;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting || entry.boundingClientRect.top > 0) continue;

        this.#observer?.disconnect();
        this.open();
      }
    });
    this.#observer.observe(marker);
  }

  /**
   * @param {Event} event - The theme editor's section select event.
   */
  #handleSectionSelect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.open();
  };

  /**
   * @param {Event} event - The theme editor's section deselect event.
   */
  #handleSectionDeselect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.refs.dialog.close();
  };
}

if (!customElements.get('age-gate-component')) customElements.define('age-gate-component', AgeGateComponent);
